import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import api from '../../services/api';
import { showError } from '../../services/alert';
import './FechamentoSafra.css';

const FechamentoSafra = () => {
  const navigate = useNavigate();

  const [safras, setSafras] = useState([]);
  const [safraId, setSafraId] = useState('');
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    carregarSafras();
  }, []);

  const carregarSafras = async () => {
    try {
      const res = await api.get('/safras');
      setSafras(res.data || []);
    } catch {
      showError('Erro ao carregar safras.');
    }
  };

  const carregarResumo = async () => {
    if (!safraId) {
      showError('Selecione uma safra.');
      return;
    }

    setLoading(true);

    try {
      const res = await api.get(`/FechamentoSafra/${safraId}/resumo`);
      setResumo(res.data);
    } catch {
      showError('Erro ao carregar fechamento da safra.');
      setResumo(null);
    } finally {
      setLoading(false);
    }
  };

  const moeda = (valor) =>
    Number(valor || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });

  const numero = (valor) =>
    Number(valor || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const estiloValor = (valor) => ({
    color: Number(valor || 0) >= 0 ? '#146c43' : '#dc3545',
    fontWeight: 700
  });

  return (
    <div className="container mt-4 fechamento-safra-page">
      <Header />

      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3 fechamento-safra-header">
          <div className="d-flex align-items-center gap-3 fechamento-safra-heading">
            <button
              className="btn btn-light btn-sm d-flex align-items-center gap-1"
              onClick={() => navigate('/menu')}
              disabled={loading}
            >
              <i className="bi bi-arrow-left text-success"></i>
              <span className="text-success fw-semibold">
                Voltar
              </span>
            </button>

            <div>
              <h5 className="mb-0">
                Fechamento da Safra
              </h5>

              <small className="text-white-50">
                Resumo financeiro, produção, romaneio e indicadores da safra
              </small>
            </div>
          </div>
        </div>

        <div className="card-body">
          <div className="row g-3 align-items-end mb-4">
            <div className="col-md-8">
              <label className="form-label fw-semibold">
                Safra
              </label>

              <select
                className="form-select"
                value={safraId}
                onChange={(e) => {
                  setSafraId(e.target.value);
                  setResumo(null);
                }}
                disabled={loading}
              >
                <option value="">
                  Selecione uma safra...
                </option>

                {safras.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.cultura}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <button
                className="btn btn-success w-100"
                onClick={carregarResumo}
                disabled={loading}
              >
                <i className="bi bi-search me-2"></i>

                {loading
                  ? 'Carregando...'
                  : 'Gerar fechamento'}
              </button>
            </div>
          </div>

          {!resumo && (
            <div className="alert alert-info">
              Selecione uma safra para visualizar o fechamento parcial.
            </div>
          )}

          {resumo && (
            <>
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Receitas
                      </small>

                      <h4 className="text-success mb-0">
                        {moeda(resumo.totalReceitas)}
                      </h4>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Despesas
                      </small>

                      <h4 className="text-danger mb-0">
                        {moeda(resumo.totalDespesas)}
                      </h4>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Resultado
                      </small>

                      <h4
                        className="mb-0"
                        style={estiloValor(resumo.resultado)}
                      >
                        {moeda(resumo.resultado)}
                      </h4>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Margem
                      </small>

                      <h4 className="text-success mb-0">
                        {numero(resumo.margemLucroPercentual)}%
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Área
                      </small>

                      <h5>
                        {numero(resumo.areaHa)} ha
                      </h5>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Total Colhido
                      </small>

                      <h5>
                        {numero(resumo.totalColhido)} kg
                      </h5>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Total Entregue
                      </small>

                      <h5>
                        {numero(resumo.totalEntregue)} kg
                      </h5>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Viagens
                      </small>

                      <h5>
                        {resumo.totalViagens}
                      </h5>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Total Vendido
                      </small>

                      <h5>
                        {numero(resumo.totalVendido)}
                      </h5>
                    </div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body">
                      <small className="text-muted">
                        Estoque Disponível
                      </small>

                      <h5>
                        {numero(resumo.estoqueDisponivel)}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-light fw-semibold">
                      Indicadores de Qualidade
                    </div>

                    <div className="card-body">
                      <p>
                        <strong>Média de umidade:</strong>{' '}
                        {numero(resumo.mediaUmidade)}%
                      </p>

                      <p className="mb-0">
                        <strong>Média de impureza:</strong>{' '}
                        {numero(resumo.mediaImpureza)}%
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-light fw-semibold">
                      Indicadores Financeiros
                    </div>

                    <div className="card-body">
                      <p>
                        <strong>Custo por kg:</strong>{' '}
                        {moeda(resumo.custoPorKg)}
                      </p>

                      <p>
                        <strong>Custo por saca:</strong>{' '}
                        {moeda(resumo.custoPorSaca)}
                      </p>

                      <p>
                        <strong>Custo por hectare:</strong>{' '}
                        {moeda(resumo.custoPorHectare)}
                      </p>

                      <p>
                        <strong>Produtividade por hectare:</strong>{' '}
                        {numero(resumo.produtividadePorHectare)}
                      </p>

                      <p>
                        <strong>Receita por hectare:</strong>{' '}
                        {moeda(resumo.receitaPorHectare)}
                      </p>

                      <p className="mb-0">
                        <strong>Lucro por hectare:</strong>{' '}
                        {moeda(resumo.lucroPorHectare)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {!!resumo.pendencias?.length && (
                <div className="alert alert-warning mt-4">
                  <strong>Pendencias para fechamento completo:</strong>

                  <ul className="mb-0 mt-2">
                    {resumo.pendencias.map((pendencia, index) => (
                      <li key={`${pendencia.tipo}-${index}`}>
                        <strong>{pendencia.tipo}:</strong>{' '}
                        {pendencia.mensagem}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!!resumo.talhoes?.length && (
                <div className="card border-0 shadow-sm mt-4">
                  <div className="card-header bg-light fw-semibold">
                    Resultado por talhão
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Talhão</th>
                          <th className="text-end">Área</th>
                          <th className="text-end">Colhido</th>
                          <th className="text-end">Vendido</th>
                          <th className="text-end">Receita</th>
                          <th className="text-end">Despesa</th>
                          <th className="text-end">Lucro</th>
                          <th className="text-end">R$/saca</th>
                          <th className="text-end">NF</th>
                        </tr>
                      </thead>

                      <tbody>
                        {resumo.talhoes.map(talhao => (
                          <tr key={talhao.talhaoId || 'sem-talhao'}>
                            <td>{talhao.talhao}</td>
                            <td className="text-end">{numero(talhao.areaHa)} ha</td>
                            <td className="text-end">{numero(talhao.totalColhido)}</td>
                            <td className="text-end">{numero(talhao.totalVendido)}</td>
                            <td className="text-end">{moeda(talhao.receitaVendas || talhao.receitaRateada)}</td>
                            <td className="text-end text-danger">{moeda(talhao.despesaRateada)}</td>
                            <td className="text-end" style={estiloValor(talhao.resultado)}>
                              {moeda(talhao.resultado)}
                            </td>
                            <td className="text-end">{moeda(talhao.custoPorSaca)}</td>
                            <td className="text-end">{talhao.totalNotasFiscais}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!!resumo.colheitas?.length && (
                <div className="card border-0 shadow-sm mt-4">
                  <div className="card-header bg-light fw-semibold">
                    Resultado estimado por colheita
                  </div>

                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Talhão</th>
                          <th>Produto</th>
                          <th className="text-end">Quantidade</th>
                          <th className="text-end">Umidade</th>
                          <th className="text-end">Impureza</th>
                          <th className="text-end">Receita est.</th>
                          <th className="text-end">Despesa</th>
                          <th className="text-end">Resultado</th>
                        </tr>
                      </thead>

                      <tbody>
                        {resumo.colheitas.map(colheita => (
                          <tr key={colheita.colheitaId}>
                            <td>{new Date(colheita.data).toLocaleDateString('pt-BR')}</td>
                            <td>{colheita.talhao}</td>
                            <td>{colheita.produtoColhido}</td>
                            <td className="text-end">
                              {numero(colheita.quantidade)} {colheita.unidade}
                            </td>
                            <td className="text-end">{numero(colheita.umidade)}%</td>
                            <td className="text-end">{numero(colheita.impureza)}%</td>
                            <td className="text-end">{moeda(colheita.receitaEstimada)}</td>
                            <td className="text-end text-danger">{moeda(colheita.despesaRateada)}</td>
                            <td className="text-end" style={estiloValor(colheita.resultadoEstimado)}>
                              {moeda(colheita.resultadoEstimado)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FechamentoSafra;
